import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Compass, Store, Building2, ArrowRight, Check } from "lucide-react";
import { api, getMe } from "../lib/api";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";

/**
 * Account-type selector — shown ONLY on first login after Google auth when
 * `role_selected_at` is not yet set. After selection the user goes either to
 * traveler signup or business/vendor welcome; the selector never reappears.
 *
 * UI:
 *   - Card 1: "Traveler / Enthusiast" — gold-bordered, selected by default
 *   - Card 2: "I am a Vendor or Business" — radio style; selecting reveals two
 *     animated CTAs ("Join as Vendor" outline / "Join as Business" filled).
 *   - Continue is disabled until a final role is chosen; inline error otherwise.
 */
export default function SelectRole() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  // Top-level pick: "user" (default) or "pro" (reveals vendor/business chooser)
  const [topPick, setTopPick] = useState("user");
  // Sub-pick when topPick === "pro": "vendor" | "business"
  const [proPick, setProPick] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) return;
    (async () => {
      const me = await getMe();
      if (!me) {
        navigate("/");
        return;
      }
      // If user has already chosen a role, skip the selector entirely
      if (me.role_selected_at) {
        const params = new URLSearchParams(window.location.search);
        const returnTo = params.get("returnTo");
        const home = me.role === "admin" ? "/admin"
                   : me.role === "vendor" ? "/vendor"
                   : me.role === "business" ? "/business"
                   : "/";
        navigate(returnTo || home, { replace: true, state: { user: me } });
        return;
      }
      setUser(me);
    })();
  }, [navigate, user]);

  const selectedRole = topPick === "user" ? "user" : proPick;

  const handleContinue = async () => {
    if (!selectedRole) {
      setError(t("select_role_error_pro"));
      return;
    }
    setError("");
    setSaving(true);
    try {
      await api.post("/auth/select-role", { role: selectedRole });
      try { localStorage.setItem("habano.role_selected", "1"); } catch { /* ignore */ }
      // iter177 — If the user landed on Get Listed while logged out and
      // was redirected through OAuth, honor the stashed destination.
      try {
        const stashed = sessionStorage.getItem("habano.auth.next");
        if (stashed) {
          sessionStorage.removeItem("habano.auth.next");
          navigate(stashed, { replace: true });
          return;
        }
      } catch { /* ignore */ }
      // Forward the caller's intended destination through select-role flow.
      const params = new URLSearchParams(window.location.search);
      const returnTo = params.get("returnTo");
      if (returnTo) {
        navigate(returnTo);
      } else if (selectedRole === "user") {
        navigate("/signup/traveler");
      } else {
        navigate(selectedRole === "vendor" ? "/vendor" : "/business");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t("select_role_save_error"));
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <main
      data-testid="select-role-page"
      className="min-h-screen pt-20 pb-24 px-5 sm:px-8 max-w-2xl mx-auto"
      style={{ background: "#171717" }}
    >
      <div className="eyebrow text-gold">{t("select_role_welcome")}</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-3 text-foreground leading-[1.05]">
        {t("select_role_pick_title")} <em className="text-gold not-italic">Habano</em>
      </h1>
      <p className="mt-4 text-foreground/60 text-sm max-w-md">
        {t("select_role_pick_sub")}
      </p>

      <div className="mt-10 space-y-4">
        {/* Card 1 — Traveler / Enthusiast (selected by default) */}
        <button
          type="button"
          data-testid="role-traveler-card"
          onClick={() => { setTopPick("user"); setProPick(null); setError(""); }}
          className={`w-full text-left border p-5 sm:p-6 transition-all duration-200 ${
            topPick === "user"
              ? "border-gold-strong bg-[rgba(232,184,75,0.06)]"
              : "border-gold-soft hover:border-gold-strong"
          }`}
          style={{ background: topPick === "user" ? "rgba(232,184,75,0.06)" : "#1D1D1D" }}
        >
          <div className="flex items-start gap-4">
            <span className="w-12 h-12 grid place-items-center rounded-full border border-gold-strong text-gold bg-[rgba(232,184,75,0.06)] shrink-0">
              <Compass className="w-5 h-5" strokeWidth={1.5} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-display text-2xl text-foreground leading-tight">
                {t("select_role_traveler_title")}
              </div>
              <div className="text-sm text-foreground/60 mt-1.5">
                {t("select_role_traveler_body")}
              </div>
            </div>
            <Radio active={topPick === "user"} testId="role-traveler-radio" />
          </div>
        </button>

        {/* Card 2 — Vendor or Business */}
        <button
          type="button"
          data-testid="role-pro-card"
          onClick={() => { setTopPick("pro"); setError(""); }}
          className={`w-full text-left border p-5 sm:p-6 transition-all duration-200 ${
            topPick === "pro"
              ? "border-gold-strong bg-[rgba(232,184,75,0.06)]"
              : "border-gold-soft hover:border-gold-strong"
          }`}
          style={{ background: topPick === "pro" ? "rgba(232,184,75,0.06)" : "#1D1D1D" }}
        >
          <div className="flex items-start gap-4">
            <span className="w-12 h-12 grid place-items-center rounded-full border border-gold-strong text-gold bg-[rgba(232,184,75,0.06)] shrink-0">
              <Building2 className="w-5 h-5" strokeWidth={1.5} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-display text-2xl text-foreground leading-tight">
                {t("select_role_pro_title")}
              </div>
              <div className="text-sm text-foreground/60 mt-1.5">
                {t("select_role_pro_body")}
              </div>
            </div>
            <Radio active={topPick === "pro"} testId="role-pro-radio" />
          </div>

          {/* Animated reveal */}
          <div
            data-testid="role-pro-reveal"
            className="grid transition-all duration-300 ease-out overflow-hidden"
            style={{ gridTemplateRows: topPick === "pro" ? "1fr" : "0fr", marginTop: topPick === "pro" ? 16 : 0 }}
          >
            <div className="min-h-0">
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <span
                  role="button"
                  tabIndex={0}
                  data-testid="role-vendor-btn"
                  onClick={(e) => { e.stopPropagation(); setProPick("vendor"); setError(""); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault(); e.stopPropagation();
                      setProPick("vendor"); setError("");
                    }
                  }}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 border text-xs uppercase tracking-widest cursor-pointer transition-colors ${
                    proPick === "vendor"
                      ? "bg-gold text-[hsl(var(--primary-foreground))] border-gold-strong"
                      : "border-gold-strong text-gold hover:bg-[rgba(232,184,75,0.08)]"
                  }`}
                  style={{ minHeight: 48 }}
                >
                  <Store className="w-4 h-4" strokeWidth={1.5} />
                  {t("select_role_join_vendor")}
                  {proPick === "vendor" && <Check className="w-3.5 h-3.5" strokeWidth={2} />}
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  data-testid="role-business-btn"
                  onClick={(e) => { e.stopPropagation(); setProPick("business"); setError(""); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault(); e.stopPropagation();
                      setProPick("business"); setError("");
                    }
                  }}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 text-xs uppercase tracking-widest cursor-pointer transition-colors ${
                    proPick === "business"
                      ? "bg-gold text-[hsl(var(--primary-foreground))] border border-gold-strong"
                      : "bg-foreground text-background border border-foreground hover:opacity-90"
                  }`}
                  style={{ minHeight: 48 }}
                >
                  <Building2 className="w-4 h-4" strokeWidth={1.5} />
                  {t("select_role_join_business")}
                  {proPick === "business" && <Check className="w-3.5 h-3.5" strokeWidth={2} />}
                </span>
              </div>
            </div>
          </div>
        </button>
      </div>

      {error && (
        <div data-testid="role-error" className="mt-4 text-sm text-red-400">
          {error}
        </div>
      )}

      <div className="mt-10 flex items-center justify-end">
        <button
          data-testid="continue-role-btn"
          disabled={saving}
          onClick={handleContinue}
          className={`inline-flex items-center gap-3 px-7 py-4 font-medium uppercase tracking-widest text-xs transition-colors ${
            selectedRole
              ? "bg-gold text-[hsl(var(--primary-foreground))] hover:opacity-90"
              : "bg-foreground/15 text-foreground/50 cursor-not-allowed"
          }`}
          style={{ minHeight: 52 }}
        >
          {t("select_role_continue_btn")}
          <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    </main>
  );
}

function Radio({ active, testId }) {
  return (
    <span
      data-testid={testId}
      aria-checked={active}
      role="radio"
      className={`w-5 h-5 rounded-full border-2 grid place-items-center shrink-0 mt-1 transition-colors ${
        active ? "border-gold-strong" : "border-foreground/30"
      }`}
    >
      {active && <span className="w-2.5 h-2.5 rounded-full bg-gold" />}
    </span>
  );
}
